<template>
  <div id="video-list">
    <base-header class="pb-6 pb-8 pt-5 pt-md-8 bg-gradient-danger header-enhanced">
      <!-- Video Statistics Cards -->
      <b-row class="mb-4">
        <b-col xl="3" md="6">
          <stats-card
            title="Total Videos"
            type="gradient-danger"
            :sub-title="stats.totalVideos ? stats.totalVideos.toLocaleString() : 'Loading...'"
            icon="ni ni-button-play"
            class="mb-4 stats-card-hover"
          >
            <template slot="footer">
              <span class="text-danger mr-2">↗ {{ stats.videoGrowth }}%</span>
              <span class="text-nowrap">vs last month</span>
            </template>
          </stats-card>
        </b-col>
        <b-col xl="3" md="6">
          <stats-card
            title="Published"
            type="gradient-success"
            :sub-title="stats.publishedVideos ? stats.publishedVideos.toLocaleString() : 'Loading...'"
            icon="ni ni-check-bold"
            class="mb-4 stats-card-hover"
          >
            <template slot="footer">
              <span class="text-success mr-2">● Active</span>
              <span class="text-nowrap">Live videos</span>
            </template>
          </stats-card>
        </b-col>
        <b-col xl="3" md="6">
          <stats-card
            title="Premium"
            type="gradient-warning"
            :sub-title="stats.premiumVideos ? stats.premiumVideos.toLocaleString() : 'Loading...'"
            icon="ni ni-trophy"
            class="mb-4 stats-card-hover"
          >
            <template slot="footer">
              <span class="text-warning mr-2">👑 Paid</span>
              <span class="text-nowrap">Premium content</span>
            </template>
          </stats-card>
        </b-col>
        <b-col xl="3" md="6">
          <stats-card
            title="Total Views"
            type="gradient-info"
            :sub-title="stats.totalViews ? stats.totalViews.toLocaleString() : 'Loading...'"
            icon="ni ni-chart-bar-32"
            class="mb-4 stats-card-hover"
          >
            <template slot="footer">
              <span class="text-info mr-2">👁️ Views</span>
              <span class="text-nowrap">All time</span>
            </template>
          </stats-card>
        </b-col>
      </b-row>

      <!-- Page Title -->
      <b-row align-v="center" class="mb-3">
        <b-col>
          <h3 class="text-white mb-0">
            <i class="ni ni-button-play mr-2"></i>Video Management
          </h3>
          <p class="text-white-50 mb-0">Manage and organize your video content</p>
        </b-col>
        <b-col class="text-right">
          <b-button
            variant="light"
            size="sm"
            class="shadow-sm"
            @click="$router.push('/admin/videos/upload')">
            <i class="ni ni-fat-add mr-1"></i>Upload Video
          </b-button>
        </b-col>
      </b-row>
    </base-header>

    <div class="container-fluid mt--7">
      <!-- Enhanced Filter Section -->
      <b-card class="mb-4 shadow-sm filter-card">
        <b-row align-v="center">
          <b-col>
            <h5 class="mb-0 text-dark">
              <i class="ni ni-zoom-split-in mr-2 text-primary"></i>
              Filter & Search Videos
            </h5>
            <small class="text-muted">Use filters to find specific videos quickly</small>
          </b-col>
        </b-row>
        <div class="mt-3">
          <FilterBar></FilterBar>
        </div>
      </b-card>
      <vuetable data-path="mydata"
                ref="vuetable"
                @vuetable:pagination-data="onPaginationData"
                @vuetable:loading="showLoader"
                @vuetable:loaded="hideLoader"
                pagination-path="pagination"
                :api-url="apiUrl"
                :fields="fields"
                :http-options="httpOptions"
                :css="css.table"
                :per-page="4"
                :append-params="moreParams"
                :query-params="{
                  sort: 'sort',
                  page: 'page',
                  perPage: 'perPage'
                }">
        <template slot="images" slot-scope="props">
          <div class="thumbnail-container" @click="itemAction('change-thumbnail', props.rowData, props.rowIndex)">
            <img :src="props.rowData.thumbnail"
                alt="Video thumbnail"
                class="video-thumbnail"
                @error="setAltThumbnail" />
            <div class="thumbnail-overlay">
              <i class="ni ni-button-play play-icon"></i>
            </div>
            <div class="thumbnail-info">
              <small class="text-white font-weight-600">{{ props.rowData.media_length }}</small>
            </div>
          </div>
        </template>
      </vuetable>
      <!-- Enhanced Pagination -->
      <b-card class="mt-4 shadow-sm pagination-card">
        <b-row align-v="center">
          <b-col md="6">
            <div class="pagination-info">
              <vuetable-pagination-info ref="paginationInfo" class="mb-0 text-muted"></vuetable-pagination-info>
            </div>
          </b-col>
          <b-col md="6" class="text-right">
            <div class="pagination-controls">
              <vuetable-pagination
                ref="pagination"
                @vuetable-pagination:change-page="onChangePage"
                :css="css.pagination">
              </vuetable-pagination>
            </div>
          </b-col>
        </b-row>
      </b-card>
    </div>
    <v-dialog/>
    <modals-container v-on:modalClose="onModalClose"/>
    <!-- Loading -->
    <loading :active.sync="loading"
             :is-full-page="true"></loading>
  </div>
</template>
<script>
  /* eslint-disable camelcase */
  const Loading = () => import('vue-loading-overlay');
  const Vuetable = () => import('vuetable-2/src/components/Vuetable');
  const VuetablePagination = () =>
  import('vuetable-2/src/components/VuetablePagination');
  const VuetablePaginationInfo = () =>
  import('vuetable-2/src/components/VuetablePaginationInfo');
  const SimpleUpload = () => import('../SimpleUpload.vue');
  const MediaListActions = () => import('./MediaListActions.vue');
  const MediaCategorySelect = () => import('./MediaCategorySelect.vue');
  const FilterBar = () => import('./FilterBar.vue');
  const MediaPremiumToggle = () => import('./MediaPremiumToggle');

  import Vue from 'vue';
  import TableStyle from '../table-bootstrap-css.js';
  import 'vue-loading-overlay/dist/vue-loading.css';
  import { mapActions } from 'vuex';
  import baseHeader from '../../BaseHeader';
  import StatsCard from '../../Cards/StatsCard.vue';


  Vue.component('media-list-actions', MediaListActions);
  Vue.component('media-category-select', MediaCategorySelect);
  Vue.component('media-premium-toggle', MediaPremiumToggle);

  export default {
    name : 'VideoList',
    data() {
      return {
        loading     : false,
        apiUrl      : process.env.VUE_APP_ROOT_API + 'api/media/video/all',
        moreParams  : {},
        css         : TableStyle,
        httpOptions : {
          headers : {
            'Authorization' : `Bearer ${this.$store.state.auth.accessToken}`,
          },
        },
        stats: {
          totalVideos: null,
          publishedVideos: null,
          premiumVideos: null,
          totalViews: null,
          videoGrowth: 0,
        },
        fields : [
          {
            name       : 'media_id',
            title      : 'Media<br>ID',
            sortField  : 'media_id',
            direction  : 'desc',
            titleClass : 'text-center',
            dataClass  : 'text-center',
          },
          {
            name       : '__slot:images',
            title      : 'Thumbnail',
            titleClass : 'text-center',
            dataClass  : 'text-center',
          },
          {
            name      : 'media_title',
            title     : 'Title',
            sortField : 'media_title',
            direction : 'desc',
          },
          {
            name       : 'media_length',
            title      : 'Length',
            titleClass : 'text-center',
            dataClass  : 'text-center',
          },
          {
            name       : '__component:media-premium-toggle',
            title      : 'Premium',
            titleClass : 'text-center premium',
            dataClass  : 'text-center',
          },
          {
            name       : '__component:media-category-select',
            title      : 'Category',
            titleClass : 'text-center category',
          },
          {
            name       : '__component:media-list-actions',
            title      : 'Actions',
            titleClass : 'text-center actions',
            dataClass  : 'text-center',
          },
          {
            name    : 'categories',
            title   : 'Categories',
            visible : false,
          },
        ],
      };
    },
    components : {
      FilterBar,
      // eslint-disable-next-line vue/no-unused-components
      SimpleUpload,
      Vuetable,
      VuetablePagination,
      VuetablePaginationInfo,
      Loading,
      baseHeader,
      StatsCard
    },
    methods : {
      ...mapActions({
        'getCategories' : 'categories/getCategories',
      }),

      /**
       * Get Transformed data
       * @param data
       */
      transform : function (data) {
        const transformed = {};
        transformed.pagination = {
          total         : data.total,
          per_page      : data.perPage,
          current_page  : data.currentPage,
          last_page     : data.lastPage,
          next_page_url : data.nextPageUrl,
          prev_page_url : data.prevPageUrl,
          from          : data.from,
          to            : data.to,
        };

        transformed.mydata = [];

        for (let i = 0; i < data.data.length; i++) {
          transformed.mydata.push({
            media_id     : data.data[i].media_id,
            media_title  : data.data[i].media_title,
            thumbnail    : data.data[i].thumbnail,
            media_length : Vue.moment.utc(Vue.moment
              .duration(data.data[i].media_length, 's')
              .asMilliseconds()).format('mm:ss'),
            premium_type     : (data.data[i].premium_type) ? 'YES' : 'NO',
            categories       : data.data[i].categories,
            inter_categories : data.data[i].inter_categories,
            notif_sent       : data.data[i].notif_sent,
            is_free          : data.data[i].is_free,
          });
        }

        return transformed;

      },

      /**
       * Set Pagination Info
       * @param paginationData
       */
      onPaginationData(paginationData) {
        this.$refs.pagination.setPaginationData(paginationData);
        this.$refs.paginationInfo.setPaginationData(paginationData);
      },

      /**
       * Change page
       * @param page
       */
      onChangePage(page) {
        this.$refs.vuetable.changePage(page);
      },

      /**
       * Show change thumbnail modal
       * @param action
       * @param data
       * @param index
       */
      // eslint-disable-next-line no-unused-vars
      itemAction(action, data, index) {
        if (action === 'change-thumbnail') {
          data.img_url = data.thumbnail;
          data.id = data.media_id;
          data.image_type = 'video';
          this.$modal.show(SimpleUpload, {
            data : data,
          }, {
            height : 'auto',
            width  : '30%',
          }, {
            'reload-table' : (event) => {
              this.$refs.vuetable.refresh();
            },
          }
          );
        }
      },

      /**
       * Close Modal
       */
      onModalClose() {
        this.$refs.vuetable.reload();
      },

      /**
       * Set Filter
       */
      onFilterSet(filterText) {
        this.moreParams = {
          'filter' : filterText,
        };
        Vue.nextTick(() => this.$refs.vuetable.refresh());
      },

      /**
       * Reset Filter
       */
      onFilterReset() {
        this.moreParams = {};
        Vue.nextTick(() => this.$refs.vuetable.refresh());
      },

      /**
       * Show Loader
       */
      showLoader() {
        this.loading = true;
      },

      /**
       * hide Loader
       */
      hideLoader() {
        this.loading = false;
      },

      /**
       * Initialize Video Statistics
       */
      initializeStats() {
        // Simulated video statistics - replace with actual API calls
        this.stats.totalVideos = 156;
        this.stats.publishedVideos = 142;
        this.stats.premiumVideos = 34;
        this.stats.totalViews = 45680;
        this.stats.videoGrowth = 23.5;
      },

      /**
       * Set Alternative Thumbnail
       */
      setAltThumbnail(event) {
        event.target.src = require('../../../assets/images/video-placeholder.png');
      },
    },
    mounted() {
      this.initializeStats();
      this.$events.$on('filter-set-category', eventData =>
        this.onFilterSet(eventData));
      this.$events.$on('filter-reset-category', () => this.onFilterReset());

      this.$store.dispatch('categories/getInteractCategories');

      if (this.$store.getters['categories/categories'] === null)
        this.getCategories();
    },
  };
</script>

<style lang="scss" scoped>
@import '../../../assets/scss/components/admin/media/video-list';

/* Enhanced Header Styles */
.baseHeader {
  margin-top: -80px;
  position: relative;
  overflow: hidden;
}

.baseHeader::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(135deg, rgba(220, 53, 69, 0.9) 0%, rgba(176, 42, 55, 0.9) 100%);
  z-index: 1;
}

.baseHeader::after {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-image:
    radial-gradient(circle at 20% 80%, rgba(220, 53, 69, 0.3) 0%, transparent 50%),
    radial-gradient(circle at 80% 20%, rgba(255, 255, 255, 0.1) 0%, transparent 50%),
    radial-gradient(circle at 40% 40%, rgba(176, 42, 55, 0.2) 0%, transparent 50%);
  z-index: 1;
}

.header-enhanced .card {
  position: relative;
  z-index: 2;
  backdrop-filter: blur(10px);
  background: rgba(255, 255, 255, 0.95) !important;
  border: 1px solid rgba(255, 255, 255, 0.2) !important;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1) !important;
}

.header-enhanced .text-white {
  color: #fff !important;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
}

.header-enhanced .card-body {
  position: relative;
  z-index: 2;
}

/* Stats Cards Hover Effects */
.stats-card-hover {
  transition: all 0.3s ease;
  cursor: pointer;
  border-radius: 12px !important;
}

.stats-card-hover:hover {
  transform: translateY(-5px);
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.15) !important;
}

.stats-card-hover .card-body {
  transition: all 0.3s ease;
}

.stats-card-hover:hover .card-body {
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0.05) 100%);
}

/* Enhanced Filter Card */
.filter-card {
  border-radius: 12px !important;
  border: none !important;
  transition: all 0.3s ease;
}

.filter-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.1) !important;
}

/* Enhanced Table Styles */
table {
  z-index: 99;
  position: relative;
  background: white;
  border-radius: 12px !important;
  overflow: hidden;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08) !important;
  animation: fadeInUp 0.8s ease-out;
}

.vuetable th {
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%) !important;
  border-bottom: 2px solid #dee2e6 !important;
  font-weight: 600 !important;
  color: #495057 !important;
  padding: 12px 8px !important;
}

.vuetable td {
  padding: 12px 8px !important;
  border-bottom: 1px solid #f1f3f4 !important;
  vertical-align: middle !important;
}

.vuetable tbody tr:hover {
  background-color: rgba(220, 53, 69, 0.05) !important;
  transform: scale(1.01);
  transition: all 0.2s ease;
}

/* Enhanced Thumbnails */
.thumbnail-container {
  position: relative;
  display: inline-block;
  cursor: pointer;
  border-radius: 8px;
  overflow: hidden;
  transition: all 0.3s ease;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
}

.thumbnail-container:hover {
  transform: scale(1.05);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.2);
}

.video-thumbnail {
  width: 150px;
  height: 100px;
  object-fit: cover;
  display: block;
  transition: all 0.3s ease;
}

.thumbnail-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: all 0.3s ease;
}

.thumbnail-container:hover .thumbnail-overlay {
  opacity: 1;
}

.play-icon {
  color: white;
  font-size: 24px;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.5);
}

.thumbnail-info {
  position: absolute;
  bottom: 8px;
  right: 8px;
  background: rgba(0, 0, 0, 0.7);
  padding: 2px 6px;
  border-radius: 4px;
  backdrop-filter: blur(4px);
}

/* Enhanced Pagination */
.pagination-card {
  border-radius: 12px !important;
  border: none !important;
  background: rgba(248, 249, 250, 0.8);
}

.pagination-info {
  padding: 16px 20px;
  font-weight: 500;
  color: #6c757d;
}

.pagination-controls {
  padding: 16px 20px;
}

/* Animations */
@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.filter-card {
  animation: fadeInUp 0.8s ease-out;
}

/* Responsive Design */
@media (max-width: 768px) {
  .stats-card-hover:hover {
    transform: none;
  }

  .filter-card:hover {
    transform: none;
  }

  .thumbnail-container:hover {
    transform: none;
  }

  .vuetable tbody tr:hover {
    transform: none;
  }

  .thumbnail-container:hover .thumbnail-overlay {
    opacity: 1;
  }

  .video-thumbnail {
    width: 120px;
    height: 80px;
  }
}

/* Legacy styles */
.baseHeader, .header {
  margin-top: -80px;
}
</style>
